import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ToastService } from '@teorema/patron';
import { Button } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { QualityPublication } from '../../../core/models/ticket.model';
import { TicketQualityService } from '../../../core/services/ticket-quality.service';
import { QualityFormComponent } from './quality-form/quality-form.component';

@Component({
  selector: 'app-ticket-quality',
  standalone: true,
  imports: [CommonModule, Button, Skeleton, Tag, Tooltip, QualityFormComponent],
  templateUrl: './ticket-quality.component.html',
  styleUrls: ['./ticket-quality.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketQualityComponent implements OnInit, OnChanges {
  @Input() ticketId!: string;
  @Input() canWrite = false;
  @Output() publicationsUpdated = new EventEmitter<QualityPublication[]>();

  @ViewChild('qualityForm') qualityForm!: QualityFormComponent;

  publications = signal<QualityPublication[]>([]);
  loading = signal(false);

  private readonly qualityService = inject(TicketQualityService);
  private readonly toastService = inject(ToastService);

  ngOnInit(): void {
    if (this.ticketId) {
      this.load();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticketId'] && !changes['ticketId'].firstChange && this.ticketId) {
      this.load();
    }
  }

  async load(): Promise<void> {
    if (!this.ticketId) return;
    this.loading.set(true);
    try {
      const data = await this.qualityService.getByTicket(this.ticketId);
      this.publications.set(data);
      this.publicationsUpdated.emit(data);
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao carregar publicações de qualidade' });
    } finally {
      this.loading.set(false);
    }
  }

  openNew(): void {
    this.qualityForm.open(null, this.ticketId);
  }

  edit(pub: QualityPublication): void {
    this.qualityForm.open(pub, this.ticketId);
  }

  async delete(pub: QualityPublication): Promise<void> {
    if (!pub.id || !confirm('Tem certeza que deseja excluir esta publicação?')) return;
    this.loading.set(true);
    try {
      await this.qualityService.delete(pub.id);
      this.toastService.show({ color: 'success', message: 'Publicação excluída com sucesso!' });
      await this.load();
    } catch {
      this.toastService.show({ color: 'error', message: 'Erro ao excluir publicação' });
    } finally {
      this.loading.set(false);
    }
  }

  onSaved(): void {
    this.load();
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  getTags(tagsStr: string | null | undefined): string[] {
    if (!tagsStr) return [];
    return tagsStr.split(',').map(t => t.trim()).filter(Boolean);
  }
}
